import React from 'react';
import { Modal, Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import { Body, CancelButton, Container, Header, Title } from './SchedulePasteDialogs';
import { ATTEND_STATUS_META } from '../privateLesson/PrivateLessonStyled';

const DANGER = '#E5484D';

const DialogContainer = styled(Container)({ width: '500px', maxWidth: 'calc(100vw - 32px)' });

const Summary = styled(Box)({
  padding: '10px 12px',
  border: '1px solid #E6ECEE',
  borderRadius: '6px',
  backgroundColor: '#fff',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: 1.5,
  color: '#262626',
});

const MemberList = styled(Box)({
  marginTop: '8px',
  maxHeight: '200px',
  overflowY: 'auto',
  border: '1px solid #E6ECEE',
  borderRadius: '6px',
  backgroundColor: '#fff',
});

const MemberRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: '36px',
  padding: '0 12px',
  borderBottom: '1px solid #F0F3F4',
  '&:last-of-type': { borderBottom: 'none' },
});

const Text = styled(Typography)({ fontSize: '14px', lineHeight: 1.5, color: '#4D4D4D' });
const SubText = styled(Typography)({ fontSize: '13px', lineHeight: 1.5, color: '#8A939C', whiteSpace: 'nowrap' });

const Footer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '0 24px 20px',
  backgroundColor: '#fafbfc',
});

const RestoreButton = styled(Button)({
  height: '40px',
  padding: '0 14px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  border: `1px solid ${DANGER}`,
  color: DANGER,
  fontSize: '14px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  '&:hover': { backgroundColor: '#FEF3F2' },
});

const DeleteButton = styled(Button)({
  height: '40px',
  padding: '0 18px',
  borderRadius: '8px',
  backgroundColor: DANGER,
  color: '#fff',
  fontSize: '14px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  '&:hover': { backgroundColor: '#C93C40' },
  '&.Mui-disabled': { color: '#fff', opacity: 0.5 },
});

// 회원 상태 칩 — 출석·결석은 지점 스케줄 상태색, 예약중 회색, 대기 옅은 회색
const STATUS_CHIP = {
  ATTENDED: { label: '출석', bg: ATTEND_STATUS_META.attended.tooltipBadgeBg, color: ATTEND_STATUS_META.attended.tooltipBadgeFont },
  ABSENT: { label: '결석', bg: ATTEND_STATUS_META.absented.tooltipBadgeBg, color: ATTEND_STATUS_META.absented.tooltipBadgeFont },
  RESERVED: { label: '예약중', bg: '#EEF0F2', color: '#5F6B73' },
  WAITING: { label: '대기', bg: '#F6F7F8', color: '#A3ABB2' },
};
// 백엔드 상태값 표기가 정해지지 않아 비슷한 이름을 함께 받는다
const STATUS_ALIAS = { ABSENTED: 'ABSENT', RESERVATION: 'RESERVED', WAIT: 'WAITING', WAITLIST: 'WAITING' };
const chipOf = status => {
  const key = String(status || '').toUpperCase();
  return STATUS_CHIP[STATUS_ALIAS[key] || key] || { label: status || '-', bg: '#EEF0F2', color: '#5F6B73' };
};
const isProcessed = status => ['ATTENDED', 'ABSENT'].includes(STATUS_ALIAS[String(status || '').toUpperCase()] || String(status || '').toUpperCase());

const StatusChip = ({ status }) => {
  const chip = chipOf(status);
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        width: '46px',
        textAlign: 'center',
        height: '20px',
        lineHeight: '20px',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: chip.bg,
        color: chip.color,
      }}>
      {chip.label}
    </Box>
  );
};

const timeRange = (start, end) => [start, end].filter(Boolean).map(t => String(t).slice(0, 5)).join('~');

/** '10월 2일(목) 10:00~10:50 · 박진현 강사 · 회원 3명' */
const summaryOf = ({ kind, event, preview }) => {
  if (kind === 'CLASS') {
    if (!preview) return '';
    const members = (preview.reservedCount || 0) + (preview.attendedCount || 0) + (preview.absentCount || 0);
    const waiting = preview.waitingCount ? ` (대기 ${preview.waitingCount}명)` : '';
    return [
      `${moment(preview.classDate, 'YYYY-MM-DD').format('M월 D일(ddd)')} ${timeRange(preview.startTime, preview.endTime)}`,
      preview.trainerName && `${preview.trainerName} 강사`,
      `회원 ${members}명${waiting}`,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  const date = event.classDate || event.registrationStartDate;
  const person = event.managerName || event.trainerName;
  return [
    `${date ? moment(date, 'YYYY-MM-DD').format('M월 D일(ddd)') : ''} ${timeRange(event.startTime, event.endTime)}`.trim(),
    person && `${person}${kind === 'CONSULTATION' ? ' 담당' : ''}`,
    kind === 'CONSULTATION' && event.memberName,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * 스케줄 카드 우클릭 '일정 삭제' 확인 모달.
 * 수업은 삭제 미리보기(preview)로 요약·회원 목록(members 가 있을 때)을 보여 주고,
 * 출석·결석 처리 회원이 있으면 [횟수 복원 후 삭제]를 함께 보여준다. 상담·기타는 요약과 [삭제]만.
 * target = { kind: 'CLASS'|'CONSULTATION'|'ETC', event, preview? }
 */
const ScheduleDeleteDialog = ({ target, busy, onConfirm, onClose }) => {
  const open = !!target;
  const preview = target?.preview;
  const members = Array.isArray(preview?.members) ? preview.members : [];
  const processedCount = (preview?.attendedCount || 0) + (preview?.absentCount || 0);
  const restorable = preview?.restorableUsage ?? processedCount;
  // 출결 처리 회원이 있어도 돌려줄 횟수가 0 이면(무제한 등) 복원 버튼을 두지 않는다
  const canRestore = target?.kind === 'CLASS' && processedCount > 0 && restorable > 0;
  const waitingPreview = target?.kind === 'CLASS' && !preview;
  // 회원(예약중·대기·출석·결석)이 있는 수업이면 무엇이 함께 사라지는지 빨간 상자로 알린다
  const hasMembers = target?.kind === 'CLASS' && !!preview
    && ((preview.reservedCount || 0) + (preview.waitingCount || 0) + processedCount) > 0;

  return (
    <Modal open={open} onClose={busy ? undefined : onClose}>
      <DialogContainer>
        <Header>
          <Title>일정 삭제</Title>
        </Header>
        <Body>
          {open && (
            <>
              <Summary>{waitingPreview ? '일정 정보를 불러오는 중입니다…' : summaryOf(target)}</Summary>
              {members.length > 0 && (
                <MemberList>
                  {members.map(member => (
                    <MemberRow key={member.memberId ?? member.memberName}>
                      <Typography noWrap sx={{ flexShrink: 0, width: '72px', fontSize: '14px', fontWeight: 700, color: '#262626' }}>
                        {member.memberName}
                      </Typography>
                      <StatusChip status={member.status} />
                      <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '12px', color: '#8A939C' }}>
                        {member.ticketName}
                      </Typography>
                      {isProcessed(member.status) && (
                        <Typography sx={{ flexShrink: 0, fontSize: '13px', fontWeight: 600, color: '#2E8A90' }}>
                          +{member.restoreUsage ?? 1}회
                        </Typography>
                      )}
                    </MemberRow>
                  ))}
                </MemberList>
              )}
              {hasMembers && (
                <Box
                  sx={{
                    mt: '10px', p: '10px 12px', borderRadius: '8px', background: '#FFF4F3',
                    border: '1px solid #F5C2BD', display: 'flex', flexDirection: 'column', gap: '4px',
                  }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DANGER }}>삭제하면 함께 사라집니다</Typography>
                  {processedCount > 0 && (
                    <Typography sx={{ fontSize: '12px', color: '#5C2B26', lineHeight: 1.5 }}>
                      · 출석 {preview.attendedCount || 0}명 · 결석 {preview.absentCount || 0}명의 출결 기록이 삭제됩니다.
                      {canRestore
                        ? ` [횟수 복원 후 삭제]는 차감된 ${restorable}회를 돌려주고, [삭제만]은 차감된 횟수를 그대로 남깁니다.`
                        : ' 돌려줄 차감 횟수는 없습니다.'}
                    </Typography>
                  )}
                  {(preview.reservedCount || 0) > 0 && (
                    <Typography sx={{ fontSize: '12px', color: '#5C2B26', lineHeight: 1.5 }}>
                      · 예약중 {preview.reservedCount}명의 예약이 취소됩니다. 회원에게 알림이 가지 않으니 직접 안내해 주세요.
                    </Typography>
                  )}
                  {(preview.waitingCount || 0) > 0 && (
                    <Typography sx={{ fontSize: '12px', color: '#5C2B26', lineHeight: 1.5 }}>
                      · 대기 {preview.waitingCount}명의 대기가 함께 사라집니다.
                    </Typography>
                  )}
                </Box>
              )}
              <Text sx={{ mt: '14px' }}>선택한 일정을 삭제할까요?</Text>
              <SubText sx={{ mt: '10px' }}>*삭제 전 반드시 회원 및 강사에게 알려 주세요.</SubText>
              <SubText sx={{ color: DANGER }}>*삭제한 일정은 복구되지 않습니다.</SubText>
            </>
          )}
        </Body>
        <Footer>
          <CancelButton disabled={busy} onClick={onClose} sx={{ px: '20px' }}>
            취소
          </CancelButton>
          {canRestore && (
            <RestoreButton disabled={busy} onClick={() => onConfirm(true)}>
              횟수 복원 후 삭제
            </RestoreButton>
          )}
          <DeleteButton disabled={busy || waitingPreview} onClick={() => onConfirm(false)}>
            {canRestore ? '삭제만' : '삭제'}
          </DeleteButton>
        </Footer>
      </DialogContainer>
    </Modal>
  );
};

export default ScheduleDeleteDialog;
